import React, { useState } from "react";
import {
  MEWMAIL_LIBRARY_IDENTIFIER,
  MEWMAIL_LIBRARY_SCRIPT_ID,
  buildBackendCode,
} from "../config/emailBackend";
import { spreadsheetUrl } from "../api/sheets";
import { useT } from "../i18n";

interface Props {
  dataSpreadsheetId: string;
  formId: string;
  responsesSheetId: string;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      console.warn("clipboard write failed:", e);
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={label}
      className="inline-flex items-center gap-1.5 text-xs border border-gray-300 bg-white rounded-md px-2.5 py-1 hover:bg-gray-100 transition-colors"
    >
      <i className={`fa-solid ${copied ? "fa-check text-green-600" : "fa-copy"}`} aria-hidden="true" />
      <span>{copied ? t("emailBackend.copied") : t("emailBackend.copy")}</span>
    </button>
  );
}

export default function EmailBackendInstructions({ dataSpreadsheetId, formId, responsesSheetId }: Props) {
  const t = useT();
  const code = buildBackendCode({ dataSpreadsheetId, formId, responsesSheetId });

  return (
    <div className="space-y-4 text-sm">
      <p className="text-gray-600">{t("emailBackend.intro")}</p>

      <ol className="space-y-3 list-decimal pl-5 text-gray-700">
        <li>
          <p>{t("emailBackend.step1")}</p>
          {responsesSheetId && (
            <a
              href={spreadsheetUrl(responsesSheetId)}
              target="_blank"
              rel="noreferrer"
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline"
            >
              <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
              <span>{t("emailBackend.openResponsesSheet")}</span>
            </a>
          )}
        </li>
        <li>
          <p>{t("emailBackend.step2", { identifier: MEWMAIL_LIBRARY_IDENTIFIER })}</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 min-w-0 break-all bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs font-mono">
              {MEWMAIL_LIBRARY_SCRIPT_ID}
            </code>
            <CopyButton text={MEWMAIL_LIBRARY_SCRIPT_ID} label={t("emailBackend.scriptIdLabel")} />
          </div>
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5 flex items-start gap-2">
            <i className="fa-solid fa-triangle-exclamation mt-0.5" aria-hidden="true" />
            <span>{t("emailBackend.shareNote")}</span>
          </p>
        </li>
        <li>
          <p>{t("emailBackend.step3")}</p>
          <div className="mt-2 rounded-md border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-3 py-1.5">
              <span className="text-xs font-medium text-gray-500">{t("emailBackend.codeLabel")}</span>
              <CopyButton text={code} label={t("emailBackend.codeLabel")} />
            </div>
            <pre className="overflow-x-auto bg-gray-900 text-gray-100 text-xs leading-relaxed p-3">
              <code>{code}</code>
            </pre>
          </div>
        </li>
        <li>{t("emailBackend.step4")}</li>
      </ol>
    </div>
  );
}
